export const DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL = 14
export const ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL = 16
export const ECOLOGICAL_ZONE_VIEWPORT_QUERY_LIMIT = 1000

const ECOLOGICAL_ZONE_STYLE_MAP = {
  '优先保护单元': {
    type: 'ecologicalZonePriority',
    label: '优先保护单元',
    color: 'rgb(77, 230, 2)'
  },
  '重点保护单元': {
    type: 'ecologicalZoneKey',
    label: '重点保护单元',
    color: 'rgb(245, 122, 122)'
  },
  '重点管控单元': {
    type: 'ecologicalZoneKey',
    label: '重点管控单元',
    color: 'rgb(245, 122, 122)'
  },
  '一般管控单元': {
    type: 'ecologicalZoneGeneral',
    label: '一般管控单元',
    color: 'rgb(246, 235, 171)'
  }
}

export function normalizeEcologicalZoneZoomLevel(value, fallback = DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL) {
  const numericValue = Number(value)
  const normalized = Number.isFinite(numericValue)
    ? Math.floor(numericValue)
    : Math.floor(Number(fallback) || DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL)

  return Math.max(1, Math.min(18, normalized))
}

export function getEcologicalZoneGeometryPrecisionName(zoomLevel) {
  const zoom = normalizeEcologicalZoneZoomLevel(zoomLevel)

  if (zoom <= 10) return 'low'
  if (zoom <= 13) return 'medium'
  if (zoom < ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL) return 'high'
  return 'exact'
}

export function normalizeEcologicalZoneType(zone = {}) {
  const rawType = String(zone.zone_type || zone.control_type || '').trim()
  const typeMapping = {
    '1': '优先保护单元',
    '2': '重点管控单元',
    '3': '一般管控单元'
  }

  return typeMapping[rawType] || rawType || '未分类'
}

export function getEcologicalZoneStyle(zone = {}) {
  const zoneType = normalizeEcologicalZoneType(zone)
  return ECOLOGICAL_ZONE_STYLE_MAP[zoneType] || {
    type: 'ecologicalZone',
    label: zoneType || '生态管控区',
    color: 'rgb(77, 230, 2)'
  }
}

export function getEcologicalZoneColor(zone = {}) {
  return getEcologicalZoneStyle(zone).color
}

export function getEcologicalZoneViewportBounds(map) {
  if (!map || typeof map.getBounds !== 'function') {
    return null
  }

  const bounds = map.getBounds()
  const southWest = bounds?.getSouthWest?.()
  const northEast = bounds?.getNorthEast?.()

  if (!southWest || !northEast) {
    return null
  }

  const minLng = Number(southWest.getLng?.() ?? southWest.lng)
  const minLat = Number(southWest.getLat?.() ?? southWest.lat)
  const maxLng = Number(northEast.getLng?.() ?? northEast.lng)
  const maxLat = Number(northEast.getLat?.() ?? northEast.lat)

  if ([minLng, minLat, maxLng, maxLat].some((value) => !Number.isFinite(value))) {
    return null
  }

  return { minLng, minLat, maxLng, maxLat }
}

function roundViewportValue(value) {
  return Number(Number(value).toFixed(4))
}

export function buildEcologicalZoneViewportKey(bounds = {}, zoomLevel) {
  const zoom = normalizeEcologicalZoneZoomLevel(zoomLevel)
  return [
    zoom,
    roundViewportValue(bounds.minLng),
    roundViewportValue(bounds.minLat),
    roundViewportValue(bounds.maxLng),
    roundViewportValue(bounds.maxLat)
  ].join(':')
}

export function getEcologicalZoneSplitDepthLimit(zoomLevel) {
  const zoom = normalizeEcologicalZoneZoomLevel(zoomLevel)
  if (zoom >= ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL) return 5
  if (zoom <= 10) return 5
  if (zoom >= 14) return 4
  if (zoom >= 11) return 4
  return 4
}

export function getEcologicalZoneMaxTileCount(zoomLevel) {
  const zoom = normalizeEcologicalZoneZoomLevel(zoomLevel)
  // 低/中缩放级别使用简化几何，允许更高的自动分片预算来补齐大范围视野；
  // 高缩放级别仍保持保守阈值，避免精细几何在前端一次性堆积过多。
  if (zoom >= ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL) return 18
  if (zoom >= 14) return 18
  if (zoom >= 11) return 20
  return 24
}

export function buildEcologicalZoneStableKey(zone = {}) {
  const id = String(zone?.id ?? '').trim()
  if (id) return `id:${id}`

  const code = String(zone?.control_unit_code ?? '').trim()
  if (code) return `code:${code}`

  const name = String(zone?.control_unit_name || zone?.name || '').trim()
  const lng = Number(zone?.center_longitude)
  const lat = Number(zone?.center_latitude)
  if (name && Number.isFinite(lng) && Number.isFinite(lat)) {
    return `name:${name}:${lng.toFixed(6)}:${lat.toFixed(6)}`
  }

  return `fallback:${JSON.stringify([
    name,
    String(zone?.zone_type || zone?.control_type || '').trim(),
    Number(zone?.area_sqm) || 0,
    Number.isFinite(lng) ? lng.toFixed(6) : '',
    Number.isFinite(lat) ? lat.toFixed(6) : ''
  ])}`
}

export function splitEcologicalZoneBounds(bounds = {}) {
  const lngSpan = Number(bounds.maxLng) - Number(bounds.minLng)
  const latSpan = Number(bounds.maxLat) - Number(bounds.minLat)

  if (!Number.isFinite(lngSpan) || !Number.isFinite(latSpan) || lngSpan <= 0 || latSpan <= 0) {
    return []
  }

  if (lngSpan >= latSpan) {
    const midLng = Number((bounds.minLng + lngSpan / 2).toFixed(6))
    if (midLng <= bounds.minLng || midLng >= bounds.maxLng) {
      return []
    }
    return [
      { ...bounds, maxLng: midLng },
      { ...bounds, minLng: midLng }
    ]
  }

  const midLat = Number((bounds.minLat + latSpan / 2).toFixed(6))
  if (midLat <= bounds.minLat || midLat >= bounds.maxLat) {
    return []
  }

  return [
    { ...bounds, maxLat: midLat },
    { ...bounds, minLat: midLat }
  ]
}

export function normalizeEcologicalZoneGeometryRecord(zone = {}, fallbackZone = {}) {
  const baseZone = fallbackZone && typeof fallbackZone === 'object' ? fallbackZone : {}
  const geometryData =
    zone.geometry ??
    zone.polygon_geojson ??
    zone.polygon_geometry ??
    baseZone.geometry ??
    baseZone.polygon_geojson ??
    baseZone.polygon_geometry ??
    null

  return {
    ...baseZone,
    ...zone,
    name: zone.name || zone.control_unit_name || baseZone.name || baseZone.control_unit_name || '未知管控区',
    control_unit_name: zone.control_unit_name || zone.name || baseZone.control_unit_name || baseZone.name || '未知管控区',
    zone_type: zone.zone_type || baseZone.zone_type || zone.control_type || baseZone.control_type || '',
    control_type: zone.control_type || baseZone.control_type || '',
    polygon_geojson: geometryData,
    geometry: geometryData
  }
}

export function buildEcologicalZoneFeatureCollection(zones = []) {
  const features = []

  zones.forEach((zone) => {
    const normalizedZone = normalizeEcologicalZoneGeometryRecord(zone)
    let geometryData = normalizedZone.polygon_geojson

    try {
      if (typeof geometryData === 'string') {
        geometryData = JSON.parse(geometryData)
      }
    } catch (_) {
      geometryData = null
    }

    if (!geometryData?.coordinates) {
      return
    }

    const style = getEcologicalZoneStyle(normalizedZone)

    features.push({
      type: 'Feature',
      geometry: geometryData,
      properties: {
        ...normalizedZone,
        geometry: undefined,
        polygon_geojson: undefined,
        renderType: style.type,
        renderColor: style.color,
        renderLabel: style.label
      }
    })
  })

  return {
    type: 'FeatureCollection',
    features
  }
}
